/**
 * Data hooks for the public checkout. Each one owns one call and one state
 * machine, so no component below this layer contains a `useEffect` that talks
 * to the network.
 *
 * The house rule for every hook here is the one `use-invoices.ts` holds:
 * **never throw and never invent data.** A hook returns a state the page can
 * render as-is; when the service says nothing, the state says so.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { payIdempotencyKey } from "./pay-api";
import {
  classifyCheckoutFailure,
  readCheckout,
  readReceipt,
  readStatus,
  type PayError,
} from "./pay-api";
import type { PayCheckoutView, PayPaymentView, PayReceiptView } from "./pay-types";

/* ---------------------------------------------------------- idempotency */

/**
 * One `Idempotency-Key` per user action — not per request.
 *
 * This is the whole reason the header exists, and getting it backwards is the
 * failure mode that hurts: a payer on a flaky connection presses "save", the
 * request is written by the service and the response is lost, they press again.
 *
 *   - A **new key per press** would create a second declaration or a second
 *     deposit address behind one invoice — deduplication with the envelope
 *     thrown away.
 *   - The **same key forever** would replay the first result against a later,
 *     different edit (the payer corrects a typo and the service answers with
 *     the old declaration, or `IDEMPOTENCY_KEY_REUSED`).
 *
 * So: the key is minted for the first attempt at a given payload and kept while
 * that attempt is unresolved. A retry of the *same* payload reuses it; a
 * changed payload, or an attempt the service has already decided
 * (`settle(true)`), mints a new one.
 */
export function useIdempotency(scope: string): {
  keyFor: (body: unknown) => string;
  settle: (decided: boolean) => void;
} {
  const pending = useRef<{ key: string; body: string } | null>(null);

  const keyFor = useCallback(
    (body: unknown) => {
      const serialised = JSON.stringify(body ?? null);
      if (pending.current && pending.current.body === serialised) return pending.current.key;
      const key = payIdempotencyKey(scope);
      pending.current = { key, body: serialised };
      return key;
    },
    [scope],
  );

  const settle = useCallback((decided: boolean) => {
    if (decided) pending.current = null;
  }, []);

  return { keyFor, settle };
}

/* ------------------------------------------------------------- checkout */

export type CheckoutState =
  | { kind: "loading" }
  | { kind: "ready"; checkout: PayCheckoutView }
  | { kind: "not-active" }
  | { kind: "closed" }
  | { kind: "rate-limited" }
  | { kind: "denied"; error: PayError }
  | { kind: "error"; error: PayError; status: number };

/**
 * Reads the checkout behind a token.
 *
 * `token === null` — no token, a malformed one, or one that arrived in a query
 * string — resolves to `not-active` **synchronously, with no request**. That is
 * the contract's rule for a malformed link and it is also why the route test
 * (which seeds no pay endpoint at all) can render `/pay` without a network
 * call: a page that fetched first and decided later would be probing the
 * service for every junk URL a scanner invents.
 */
export function usePayCheckout(token: string | null): {
  state: CheckoutState;
  refresh: () => void;
} {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<CheckoutState>(() =>
    token === null ? { kind: "not-active" } : { kind: "loading" },
  );

  useEffect(() => {
    if (token === null) {
      setState({ kind: "not-active" });
      return;
    }
    const controller = new AbortController();
    let live = true;
    setState({ kind: "loading" });

    void (async () => {
      const result = await readCheckout(token, { signal: controller.signal });
      // The result is dropped when the token changed, the attempt was
      // superseded, or the component unmounted — an aborted request reports a
      // transport failure, and reporting that as the payer's error state would
      // be a lie about what happened.
      if (!live) return;
      if (result.ok) {
        setState({ kind: "ready", checkout: result.data });
        return;
      }
      setState(classifyCheckoutFailure(result.error, result.status));
    })();

    return () => {
      live = false;
      controller.abort();
    };
  }, [token, attempt]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, refresh };
}

/* --------------------------------------------------------------- status */

/** The contract's cadence: an ETag poll every 5 s while the payment confirms. */
export const PAY_STATUS_INTERVAL_MS = 5_000;
/** Backoff ceiling after consecutive failures. */
export const PAY_STATUS_MAX_INTERVAL_MS = 60_000;
/** Consecutive failures after which live updates stop and the page says so. */
export const PAY_STATUS_MAX_FAILURES = 5;

export interface PayStatusPolling {
  payment: PayPaymentView;
  /** True while a poll is scheduled — the page shows its "checking…" note. */
  polling: boolean;
  /** True once polling gave up; the last known status stands and is labelled. */
  stale: boolean;
  /** Poll immediately (the payer pressed "check now"). */
  refresh: () => void;
}

/**
 * Polls `GET …/status` while the payment is `confirming`, and only then.
 *
 * Four decisions, each deliberate:
 *
 *   - **Only while `confirming`.** The spec is exact: "polls `GET …/status`
 *     with an ETag every 5 s while `confirming`". An `unpaid` invoice is not
 *     polled, so this page does not sit on the issuer's rate limit waiting for
 *     a payment that may never come.
 *   - **Conditional.** The last `ETag` goes back as `If-None-Match`; a `304`
 *     changes nothing and costs the service no body.
 *   - **Bounded.** Consecutive failures back off 5s, 10s, 20s, 40s, then stop.
 *     A page that retried forever would be indistinguishable from an attack on
 *     a link it can no longer read.
 *   - **Honest when it stops.** `stale` is surfaced; the last status is shown
 *     with a note and a manual refresh rather than silently frozen.
 */
export function usePayStatus(token: string, initial: PayPaymentView): PayStatusPolling {
  const [payment, setPayment] = useState<PayPaymentView>(initial);
  const [stale, setStale] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // The server's status re-seeds this hook when a fresh checkout read changes
  // it — that is a real change, unlike a re-render. Kept in a ref so the resync
  // effect depends only on the primitive key below.
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const initialKey = `${initial.status}:${initial.confirmations ?? ""}:${initial.requiredConfirmations ?? ""}`;
  useEffect(() => {
    setPayment(initialRef.current);
    setStale(false);
  }, [initialKey]);

  const confirming = payment.status === "confirming";

  useEffect(() => {
    if (!confirming) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    let etag: string | null = null;

    const schedule = (ms: number) => {
      timer = setTimeout(() => void poll(), ms);
    };

    const poll = async () => {
      const result = await readStatus(token, { etag });
      if (cancelled) return;
      if (result.ok) {
        failures = 0;
        if (result.changed) {
          etag = result.etag;
          setPayment(result.payment);
        }
        schedule(PAY_STATUS_INTERVAL_MS);
        return;
      }
      failures += 1;
      if (failures >= PAY_STATUS_MAX_FAILURES) {
        setStale(true);
        return;
      }
      schedule(Math.min(PAY_STATUS_INTERVAL_MS * 2 ** failures, PAY_STATUS_MAX_INTERVAL_MS));
    };

    schedule(PAY_STATUS_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [token, confirming, attempt]);

  const refresh = useCallback(() => {
    setStale(false);
    setAttempt((n) => n + 1);
  }, []);

  return { payment, polling: confirming && !stale, stale, refresh };
}

/* -------------------------------------------------------------- receipt */

export type ReceiptState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; receipt: PayReceiptView }
  | { kind: "unavailable"; error: PayError }
  | { kind: "error"; error: PayError };

/**
 * Fetches the receipt once the payment is final. `enabled` is driven by the
 * payment status: while screening is pending the page shows the review note and
 * this hook stays idle, so no receipt request is made for a payment that has
 * not been released (and no receipt URL can be guessed at).
 */
export function useReceipt(token: string, enabled: boolean): { state: ReceiptState } {
  const [state, setState] = useState<ReceiptState>({ kind: "idle" });

  useEffect(() => {
    if (!enabled) {
      setState({ kind: "idle" });
      return;
    }
    const controller = new AbortController();
    let live = true;
    setState({ kind: "loading" });

    void (async () => {
      const result = await readReceipt(token, { signal: controller.signal });
      if (!live) return;
      if (result.ok) {
        setState(result.data.available ? { kind: "ready", receipt: result.data } : { kind: "unavailable", error: { reasonCode: "RECEIPT_PENDING" } });
        return;
      }
      const notYet = result.status === 404 || result.status === 409;
      setState(notYet ? { kind: "unavailable", error: result.error } : { kind: "error", error: result.error });
    })();

    return () => {
      live = false;
      controller.abort();
    };
  }, [token, enabled]);

  return { state };
}
